/**
 * SOVRANOVA — Journal View
 * Private, encrypted journal. AI reflects back one warm sentence per entry.
 * Mood tracking. Persists to Supabase journal_entries table.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../../services/api'
import { resolveRegion, getCrisisResources, HELPLINE_DIRECTORY_URL } from '../../services/crisisResources'
import { localCrisisCheck } from '../../services/crisisCheck'

const MOODS = [
  { value: 'calm',       label: '😌 Calm' },
  { value: 'anxious',    label: '😰 Anxious' },
  { value: 'heavy',      label: '😔 Heavy' },
  { value: 'grateful',   label: '🙏 Grateful' },
  { value: 'frustrated', label: '😤 Frustrated' },
  { value: 'hopeful',    label: '✨ Hopeful' },
  { value: 'numb',       label: '😶 Numb' },
  { value: 'energised',  label: '⚡ Energised' },
]

export function JournalView({ onClose }) {
  const [entries,  setEntries]  = useState([])
  const [content,  setContent]  = useState('')
  const [mood,     setMood]     = useState(null)
  const [saving,   setSaving]   = useState(false)
  const [view,     setView]     = useState('write') // write | history
  const [loading,  setLoading]  = useState(false)
  // D0-G3: set when the server (or, offline, the local net) flags the entry as
  // high/critical. Holds the crisis response + resources to show in place of
  // the usual silent hand-off to the history list.
  const [crisis,   setCrisis]   = useState(null)
  const textareaRef = useRef(null)

  useEffect(() => {
    if (view === 'history') loadEntries()
  }, [view])

  const loadEntries = async () => {
    setLoading(true)
    try {
      const { entries: data } = await api.get('/journal/entries?limit=20')
      setEntries(data || [])
    } catch { /* non-fatal */ }
    finally { setLoading(false) }
  }

  const autoResize = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 320) + 'px'
  }, [])

  const handleSave = async () => {
    if (!content.trim() || saving) return
    setSaving(true)
    const written = content.trim()
    try {
      // D0-G3: the entry is screened server-side before it is reflected on. A
      // high/critical screen returns `crisis` with this region's real
      // resources; an ordinary entry is untouched and behaves exactly as
      // before. Region is sent so those resources are the person's own.
      const r = await api.post('/journal/entry', { content: written, mood, region: resolveRegion() })
      setContent('')
      setMood(null)
      if (textareaRef.current) textareaRef.current.style.height = 'auto'

      // Offline: /journal/entry is queued for replay, so nothing screened it at
      // the moment it was written. Run the local net now — the same mirror of
      // the hard-trigger list useSession uses for queued messages.
      const offlineCrisis = r && r._queued && localCrisisCheck(written)

      if ((r && r.crisis) || offlineCrisis) {
        // Stay on this screen with support in front of them. Bouncing someone
        // who has just written something hard into a history list is precisely
        // the behaviour this gate exists to remove.
        setCrisis(r && r.crisis ? r : { offline: true })
      } else {
        setView('history')
      }
    } catch { /* non-fatal */ }
    finally { setSaving(false) }
  }

  const handleDelete = async (id) => {
    try {
      await api.post('/journal/entry/' + id, {}) // uses DELETE via fetch workaround
      setEntries(prev => prev.filter(e => e.id !== id))
    } catch { /* non-fatal */ }
  }

  const formatDate = (iso) => {
    const d = new Date(iso)
    return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) +
      ' · ' + d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={onClose}>← Back</button>
        <div style={s.title}>📖 Journal</div>
        <div style={s.tabs}>
          <button style={{ ...s.tab, ...(view === 'write' ? s.tabActive : {}) }} onClick={() => setView('write')}>Write</button>
          <button style={{ ...s.tab, ...(view === 'history' ? s.tabActive : {}) }} onClick={() => setView('history')}>Past</button>
        </div>
      </div>

      {view === 'write' ? (
        <div style={s.writeArea}>
          {crisis && <JournalCrisisCard crisis={crisis} onDismiss={() => setCrisis(null)} />}
          {/* Mood selector */}
          <div style={s.moodSection}>
            <p style={s.moodLabel}>How are you feeling right now?</p>
            <div style={s.moodGrid}>
              {MOODS.map(m => (
                <button key={m.value}
                  style={{ ...s.moodBtn, ...(mood === m.value ? s.moodBtnActive : {}) }}
                  onClick={() => setMood(mood === m.value ? null : m.value)}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Journal textarea */}
          <div style={s.editorWrap}>
            <textarea
              ref={textareaRef}
              style={s.editor}
              value={content}
              onChange={e => { setContent(e.target.value); autoResize() }}
              placeholder="This is your space. Write anything — it stays in your account.&#10;&#10;What's on your mind today?"
              rows={6}
            />
          </div>

          {/* Was "private and encrypted. Never shared." — not true: each entry is
            sent to our AI provider to write the reflection that comes back. */}
        <p style={s.privacy}>Private to your account and never sold. Your entry is sent to SOVRANOVA's AI provider to write your reflection.</p>

          <button style={{ ...s.saveBtn, opacity: !content.trim() || saving ? 0.5 : 1 }}
            onClick={handleSave}
            disabled={!content.trim() || saving}>
            {saving ? 'Saving...' : 'Save Entry ◎'}
          </button>
        </div>
      ) : (
        <div style={s.historyArea}>
          {loading ? (
            <div style={s.emptyState}>◎</div>
          ) : entries.length === 0 ? (
            <div style={s.emptyState}>
              <p>No entries yet.</p>
              <p style={{ fontSize: '0.875rem', color: 'var(--mid-grey)', marginTop: '0.5rem' }}>
                Your first entry is waiting.
              </p>
            </div>
          ) : (
            entries.map(entry => (
              <div key={entry.id} style={s.entryCard}>
                <div style={s.entryHeader}>
                  <span style={s.entryDate}>{formatDate(entry.created_at)}</span>
                  {entry.mood && (
                    <span style={s.entryMood}>
                      {MOODS.find(m => m.value === entry.mood)?.label || entry.mood}
                    </span>
                  )}
                </div>
                <p style={s.entryContent}>{entry.content}</p>
                {entry.reflection && (
                  <div style={s.reflection}>
                    <span style={s.reflectionMark}>◎</span>
                    <p style={s.reflectionText}>{entry.reflection}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// D0-G3: shown in place of the usual warm reflection when an entry screens
// high/critical. The numbers come from the SAME deterministic registry as the
// always-on lifeline pill — never from a model, never hard-coded to a country,
// and correct offline.
function JournalCrisisCard({ crisis, onDismiss }) {
  const res = getCrisisResources(resolveRegion())
  return (
    <div style={s.crisisCard} role="alert">
      <p style={s.crisisLead}>You don't have to face this alone.</p>
      <div style={s.crisisBody}>
        {crisis.crisisResponse || 'Please reach out — support is available right now.'}
      </div>
      <div style={s.crisisLinks}>
        {res.primary.tel
          ? <a href={`tel:${res.primary.tel}`} style={s.crisisLink}>Call {res.primary.label} {res.primary.number}</a>
          : <a href={HELPLINE_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" style={s.crisisLink}>{res.primary.label}</a>}
        {res.emergency.tel
          ? <a href={`tel:${res.emergency.tel}`} style={s.crisisLink}>Emergency {res.emergency.number}</a>
          : <span style={s.crisisLink}>Emergency — {res.emergency.number}</span>}
      </div>
      <p style={s.crisisNote}>
        Not in {res.region}?{' '}
        <a href={HELPLINE_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" style={s.crisisNoteLink}>Find support in your country</a>.
        {crisis.offline
          ? " Your entry is saved on this device and will sync when you're back online."
          : ' Your entry is saved.'}
      </p>
      <button type="button" style={s.crisisDismiss} onClick={onDismiss}>Close</button>
    </div>
  )
}

const s = {
  // Organic restyle (Board J1, 2026-08-13): cream writing room. Values only.
  crisisCard: {
    background: 'rgba(192,57,43,.10)', border: '1.5px solid rgba(192,57,43,.38)',
    borderRadius: '18px', padding: '1rem 1.15rem',
    display: 'flex', flexDirection: 'column', gap: '0.6rem',
    color: 'var(--og-n900, #2e2b25)',
  },
  crisisLead: { margin: 0, fontWeight: 700, fontSize: '0.95rem' },
  crisisBody: { whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.55 },
  crisisLinks: { display: 'flex', gap: '0.9rem', flexWrap: 'wrap' },
  crisisLink: { color: '#8c2f22', fontWeight: 700, fontSize: '0.9rem', textDecoration: 'none' },
  crisisNote: { margin: 0, fontSize: '0.78rem', opacity: 0.85, lineHeight: 1.5 },
  crisisNoteLink: { color: '#8c2f22', textDecoration: 'underline' },
  crisisDismiss: {
    alignSelf: 'flex-start', padding: '0.4rem 0.9rem', borderRadius: '999px',
    border: '1px solid rgba(192,57,43,.35)', background: 'transparent',
    color: '#8c2f22', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  page: {
    display: 'flex', flexDirection: 'column', height: '100%',
    background: 'var(--og-bg, #f5ead8)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0.875rem 1.25rem',
    borderBottom: '1.5px solid var(--og-n200, #eee7db)',
    background: 'var(--og-bg, #f5ead8)', flexShrink: 0,
    flexWrap: 'wrap', gap: '0.5rem',
  },
  backBtn: {
    background: 'transparent', border: 'none', color: 'var(--og-n500, #a19786)',
    fontSize: '0.875rem', cursor: 'pointer',
  },
  title: { fontFamily: 'var(--og-fh, Georgia, serif)', fontWeight: 400, fontSize: '1.05rem', color: 'var(--og-a900, #402310)' },
  tabs: { display: 'flex', gap: '4px', background: 'var(--og-n100, #f9f4ed)', borderRadius: '999px', padding: '4px' },
  tab: {
    padding: '0.32rem 0.85rem', border: 'none', borderRadius: '999px',
    background: 'transparent', fontSize: '0.8rem', cursor: 'pointer',
    color: 'var(--og-n700, #645c50)', fontWeight: 600, fontFamily: 'inherit',
  },
  tabActive: { background: 'var(--og-accent, #c67139)', color: 'var(--og-n100, #f9f4ed)', fontWeight: 600 },

  writeArea: {
    flex: 1, overflowY: 'auto', padding: '1.25rem',
    display: 'flex', flexDirection: 'column', gap: '1.25rem',
  },
  moodSection: {},
  moodLabel: { fontSize: '0.875rem', color: 'var(--mid-grey)', marginBottom: '0.625rem' },
  moodGrid: {
    display: 'flex', flexWrap: 'wrap', gap: '0.4rem',
  },
  moodBtn: {
    padding: '0.5rem 1rem', border: 'none',
    borderRadius: '999px', fontSize: '0.82rem', cursor: 'pointer',
    background: 'var(--og-n100, #f9f4ed)', color: 'var(--og-text, #201e1d)',
    fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.15s',
  },
  moodBtnActive: {
    background: 'var(--og-accent2, #7a8a5e)', color: '#fff', fontWeight: 600,
  },
  editorWrap: { flex: 1 },
  editor: {
    width: '100%', minHeight: 160,
    padding: '1rem 1.1rem', border: '1px solid rgba(32,30,29,.12)',
    borderRadius: '18px', fontSize: '1rem', lineHeight: 1.7,
    fontFamily: 'inherit', resize: 'none', background: '#fff',
    color: 'var(--og-n900, #2e2b25)',
  },
  privacy: { fontSize: '0.75rem', color: 'var(--og-n500, #a19786)', textAlign: 'center' },
  saveBtn: {
    padding: '0.9rem', background: 'var(--og-accent2, #7a8a5e)', color: 'white',
    border: 'none', borderRadius: '999px', fontSize: '0.98rem',
    fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },

  historyArea: {
    flex: 1, overflowY: 'auto', padding: '1.25rem',
    display: 'flex', flexDirection: 'column', gap: '1rem',
  },
  emptyState: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    color: 'var(--mid-grey)', fontSize: '2rem', opacity: 0.3,
    paddingTop: '4rem',
  },
  entryCard: {
    background: 'var(--og-n100, #f9f4ed)', borderRadius: '22px', padding: '1rem 1.2rem',
    boxShadow: 'none',
    display: 'flex', flexDirection: 'column', gap: '0.625rem',
  },
  entryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem' },
  entryDate: { fontSize: '0.74rem', color: 'var(--og-n500, #a19786)' },
  entryMood: { fontSize: '0.74rem', background: 'var(--og-s100, #f0fae1)', color: 'var(--og-s800, #3d472b)', padding: '0.22rem 0.7rem', borderRadius: '999px', fontWeight: 600 },
  entryContent: { fontSize: '0.95rem', lineHeight: 1.65, color: 'var(--og-n800, #474238)', whiteSpace: 'pre-wrap' },
  reflection: {
    display: 'flex', gap: '0.625rem', alignItems: 'flex-start',
    background: 'var(--og-s100, #f0fae1)', borderRadius: '16px',
    padding: '0.7rem 0.9rem', marginTop: '0.25rem', color: 'var(--og-s900, #272e1b)',
  },
  reflectionMark: { color: 'var(--sage)', fontSize: '0.85rem', flexShrink: 0, marginTop: '0.1rem' },
  reflectionText: { fontSize: '0.875rem', color: 'var(--sage-dark)', lineHeight: 1.55, fontStyle: 'italic' },
}
